import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocaleProvider, SUPPORTED_LOCALES } from "@/contexts/LocaleContext";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, lazy, Suspense } from "react";
import { initAnalytics, trackPageView, captureLandingPage } from "@/lib/analytics";

import PublicLayout from "@/layouts/PublicLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Critical public pages — loaded eagerly for fast first paint
import HomePage from "@/pages/public/HomePage";
import ProgramsPage from "@/pages/public/ProgramsPage";
import ContactPage from "@/pages/public/ContactPage";

// Lazy-loaded public pages
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const ProgramDetailPage = lazy(() => import("@/pages/public/ProgramDetailPage"));
const ProgramDetailPage_v2 = lazy(() => import("@/pages/public/ProgramDetailPage_v2"));
const ProgramPreviewPage = lazy(() => import("@/pages/public/ProgramPreviewPage"));
const AdmissionsPage = lazy(() => import("@/pages/public/AdmissionsPage"));
const SeminarsPage = lazy(() => import("@/pages/public/SeminarsPage"));
const TestimonialsPage = lazy(() => import("@/pages/public/TestimonialsPage"));
const FAQPage = lazy(() => import("@/pages/public/FAQPage"));
const BookingPage = lazy(() => import("@/pages/public/BookingPage"));
const ApplyPage = lazy(() => import("@/pages/public/ApplyPage"));
const SignContractPage = lazy(() => import("@/pages/public/SignContractPage"));
const RescheduleBookingPage = lazy(() => import("@/pages/public/RescheduleBookingPage"));
const LeadCapturePage = lazy(() => import("@/pages/public/LeadCapturePage"));
const EducatorsPage = lazy(() => import("@/pages/public/EducatorsPage"));
const EducatorDetailPage = lazy(() => import("@/pages/public/EducatorDetailPage"));
const MethodologyPage = lazy(() => import("@/pages/public/MethodologyPage"));
const WhyMiselPage = lazy(() => import("@/pages/public/WhyMiselPage"));
const RegistrationForm = lazy(() => import("@/pages/public/RegistrationForm"));
const ContinuingEducationForm = lazy(() => import("@/pages/public/ContinuingEducationForm"));
const SalonEmployeeSearchForm = lazy(() => import("@/pages/public/SalonEmployeeSearchForm"));

// Lazy-loaded auth pages
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));

// Lazy-loaded apply flow
const ApplicationForm = lazy(() => import("@/pages/apply/ApplicationForm"));
const DocumentUpload = lazy(() => import("@/pages/apply/DocumentUpload"));
const ApplicationStatus = lazy(() => import("@/pages/apply/ApplicationStatus"));
const PaymentPage = lazy(() => import("@/pages/apply/PaymentPage"));
const PaymentSuccess = lazy(() => import("@/pages/apply/PaymentSuccess"));

// Lazy-loaded portal
const StudentDashboard = lazy(() => import("@/pages/portal/StudentDashboard"));
const AttendancePage = lazy(() => import("@/pages/portal/AttendancePage"));
const GradesPage = lazy(() => import("@/pages/portal/GradesPage"));
const RequestsPage = lazy(() => import("@/pages/portal/RequestsPage"));
const StudentFinancePage = lazy(() => import("@/pages/portal/FinancePage"));
const AlumniReferralsPage = lazy(() => import("@/pages/portal/ReferralsPage"));

// Lazy-loaded admin (entire admin section)
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const LeadsPage = lazy(() => import("@/pages/admin/LeadsPage"));
const ApplicationsPage = lazy(() => import("@/pages/admin/ApplicationsPage"));
const ProgramsAdmin = lazy(() => import("@/pages/admin/ProgramsAdmin"));
const TranslationsPage = lazy(() => import("@/pages/admin/TranslationsPage"));
const GlossaryPage = lazy(() => import("@/pages/admin/GlossaryPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const FinancePage = lazy(() => import("@/pages/admin/FinancePage"));
const SessionsPage = lazy(() => import("@/pages/admin/SessionsPage"));
const AdminRequestsPage = lazy(() => import("@/pages/admin/AdminRequestsPage"));
const AlumniPage = lazy(() => import("@/pages/admin/AlumniPage"));
const CampaignsPage = lazy(() => import("@/pages/admin/CampaignsPage"));
const MarketingPage = lazy(() => import("@/pages/admin/MarketingPage"));
const ReferralsPage = lazy(() => import("@/pages/admin/ReferralsPage"));
const SEOPage = lazy(() => import("@/pages/admin/SEOPage"));
const SEOHealthPage = lazy(() => import("@/pages/admin/SEOHealthPage"));
const CommunicationsPage = lazy(() => import("@/pages/admin/CommunicationsPage"));
const BIPage = lazy(() => import("@/pages/admin/BIPage"));
const ConnectionsPage = lazy(() => import("@/pages/admin/ConnectionsPage"));
const SystemHealthPage = lazy(() => import("@/pages/admin/SystemHealthPage"));
const MagazineAdmin = lazy(() => import("@/pages/admin/MagazineAdmin"));
const DistributionPage = lazy(() => import("@/pages/admin/DistributionPage"));
const TrackingSettingsPage = lazy(() => import("@/pages/admin/TrackingSettingsPage"));
const TrackingDiagnosticsPage = lazy(() => import("@/pages/admin/TrackingDiagnosticsPage"));
const SEOIntelligencePage = lazy(() => import("@/pages/admin/SEOIntelligencePage"));
const SiteContentPage = lazy(() => import("@/pages/admin/SiteContentPage"));
const AdminTestimonialsPage = lazy(() => import("@/pages/admin/AdminTestimonialsPage"));
const AdminEducatorsPage = lazy(() => import("@/pages/admin/AdminEducatorsPage"));
const AdminAuthorityPage = lazy(() => import("@/pages/admin/AdminAuthorityPage"));
const StudentMigrationPage = lazy(() => import("@/pages/admin/StudentMigrationPage"));
const AppointmentsPage = lazy(() => import("@/pages/admin/AppointmentsPage"));
const AdmissionsPipelinePage = lazy(() => import("@/pages/admin/AdmissionsPipelinePage"));
const LeadPipelinePage = lazy(() => import("@/pages/admin/LeadPipelinePage"));
const ContactImportPage = lazy(() => import("@/pages/admin/ContactImportPage"));
const SegmentationPage = lazy(() => import("@/pages/admin/SegmentationPage"));

// Lazy-loaded magazine (named exports need wrapper)
const MagazinePageLazy = lazy(() => import("@/pages/public/MagazinePage").then(m => ({ default: m.MagazinePage })));
const ArticlePageLazy = lazy(() => import("@/pages/public/MagazinePage").then(m => ({ default: m.ArticlePage })));

// Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
    <div className="animate-pulse text-muted-foreground text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>MISEL GROUP</div>
  </div>
);

// V2 landing pages: tries structured content first, falls back to legacy
function ProgramDetailPageRouter() {
  return <ProgramDetailPage_v2 />;
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading, mustResetPassword } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!user) return <Navigate to="/auth/login" replace />;
  if (mustResetPassword) return <Navigate to="/auth/reset-password" replace />;
  if (requiredRole && !user.roles?.some(r => ['super_admin', 'owner_director', requiredRole].includes(r))) {
    return <Navigate to="/en" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin, mustResetPassword } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!user || !isAdmin) return <Navigate to="/auth/login" replace />;
  if (mustResetPassword) return <Navigate to="/auth/reset-password" replace />;
  return children;
}

// Analytics page view tracker
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    const locale = location.pathname.split('/')[1] || 'en';
    trackPageView(location.pathname, document.title, locale);
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    initAnalytics();
    captureLandingPage();
  }, []);
  
  return (
    <HelmetProvider>
      <AuthProvider>
        <LocaleProvider>
          <BrowserRouter>
            <AnalyticsTracker />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Root redirect */}
              <Route path="/" element={<Navigate to="/en" replace />} />

              {/* Public routes with locale */}
              <Route path="/:locale" element={<PublicLayout />}>
                <Route index element={<HomePage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="programs" element={<ProgramsPage />} />
                <Route path="programs/preview/:programId" element={<ProgramPreviewPage />} />
                <Route path="programs/:slug" element={<ProgramDetailPageRouter />} />
                <Route path="admissions" element={<AdmissionsPage />} />
                <Route path="booking" element={<BookingPage />} />
                <Route path="booking/manage" element={<RescheduleBookingPage />} />
                <Route path="inquiry" element={<LeadCapturePage />} />
                <Route path="apply" element={<ApplyPage />} />
                <Route path="sign-contract" element={<SignContractPage />} />
                <Route path="seminars" element={<SeminarsPage />} />
                <Route path="testimonials" element={<TestimonialsPage />} />
                <Route path="faq" element={<FAQPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="magazine" element={<MagazinePageLazy />} />
                <Route path="magazine/:slug" element={<ArticlePageLazy />} />
                <Route path="educators" element={<EducatorsPage />} />
                <Route path="educators/:slug" element={<EducatorDetailPage />} />
                <Route path="methodology" element={<MethodologyPage />} />
                <Route path="why-misel" element={<WhyMiselPage />} />
                <Route path="aitisi-eggrafis" element={<RegistrationForm />} />
                <Route path="aitisi-eggrafis-metekpaideysis-eidikeysis" element={<ContinuingEducationForm />} />
                <Route path="salon-employee-search-form" element={<SalonEmployeeSearchForm />} />
              </Route>

              {/* Auth */}
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/register" element={<RegisterPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

              {/* Application flow */}
              <Route path="/apply/form" element={<ProtectedRoute><ApplicationForm /></ProtectedRoute>} />
              <Route path="/apply/documents" element={<ProtectedRoute><DocumentUpload /></ProtectedRoute>} />
              <Route path="/apply/status" element={<ProtectedRoute><ApplicationStatus /></ProtectedRoute>} />
              <Route path="/apply/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
              <Route path="/apply/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

              {/* Student portal */}
              <Route path="/portal/dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
              <Route path="/portal/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
              <Route path="/portal/grades" element={<ProtectedRoute><GradesPage /></ProtectedRoute>} />
              <Route path="/portal/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
              <Route path="/portal/finance" element={<ProtectedRoute><StudentFinancePage /></ProtectedRoute>} />
              <Route path="/portal/referrals" element={<ProtectedRoute><AlumniReferralsPage /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="applications" element={<AdmissionsPipelinePage />} />
                <Route path="lead-pipeline" element={<LeadPipelinePage />} />
                <Route path="programs" element={<ProgramsAdmin />} />
                <Route path="translations" element={<TranslationsPage />} />
                <Route path="glossary" element={<GlossaryPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="finance" element={<FinancePage />} />
                <Route path="sessions" element={<SessionsPage />} />
                <Route path="requests" element={<AdminRequestsPage />} />
                <Route path="alumni" element={<AlumniPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="marketing" element={<MarketingPage />} />
                <Route path="referrals" element={<ReferralsPage />} />
                <Route path="seo" element={<SEOPage />} />
                <Route path="seo-health" element={<SEOHealthPage />} />
                <Route path="communications" element={<CommunicationsPage />} />
                <Route path="bi" element={<BIPage />} />
                <Route path="connections" element={<ConnectionsPage />} />
                <Route path="system-health" element={<SystemHealthPage />} />
                <Route path="magazine" element={<MagazineAdmin />} />
                <Route path="distribution" element={<DistributionPage />} />
                <Route path="tracking" element={<TrackingSettingsPage />} />
                <Route path="tracking-diagnostics" element={<TrackingDiagnosticsPage />} />
                <Route path="seo-intelligence" element={<SEOIntelligencePage />} />
                <Route path="site-content" element={<SiteContentPage />} />
                <Route path="testimonials" element={<AdminTestimonialsPage />} />
                <Route path="educators" element={<AdminEducatorsPage />} />
                <Route path="authority" element={<AdminAuthorityPage />} />
                <Route path="student-migration" element={<StudentMigrationPage />} />
                <Route path="contacts" element={<ContactImportPage />} />
                <Route path="segments" element={<SegmentationPage />} />
                <Route path="documents" element={<ApplicationsPage />} />
                <Route path="students" element={<LeadsPage />} />
                <Route path="analytics" element={<AdminDashboard />} />
                <Route path="audit" element={<AdminDashboard />} />
                <Route path="appointments" element={<AppointmentsPage />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/en" replace />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" />
        </LocaleProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
